import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from "@/router";
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const refListTable = ref(null)
  const toast = useToast()

  const tableColumns = [
    {
      label: 'dmp.description',
      key: 'description',
      sortable: false
    },
    {
      label: 'types_alarms.icon',
      key: 'icon',
      sortable: false
    },
    {
      label: 'types_alarms.color_icon',
      key: 'color_icon',
      sortable: false
    },
    {
      label: 'dmp.eventDefinition',
      key: 'event_definition',
      sortable: false
    },
    {
      label: 'dmp.eventType',
      key: 'event_type',
      sortable: false
    },
    {
      label: 'dmp.deviceType',
      key: 'device_type',
      sortable: false
    },
    {
      label: 'dmp.deviceId',
      key: 'device_id',
      sortable: false
    },
    {
      label: 'dmp.alarm_type_sbo',
      key: 'alarm_type',
      sortable: true
    },
    {
      label: 'dmp.tag',
      key: 'id_device_tag',
      sortable: false
    },
    {
      label: 'dmp.dispatchAlarm',
      key: 'dispatch_alarm',
      sortable: true
    },
  ]

  if (ability.can('update', 'Dmp') || ability.can('delete', 'Dmp')) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('description')
  const isSortDirDesc = ref(false)
  const idPanel = router.currentRoute.params.id_panel;

  store.dispatch('type_alarm/getAll')
  store.dispatch('dmp_events_definition/getAll')

  store.dispatch('tenant/getDeviceTags')

  const getTagName = (id) => {
    if (!id) return ''
    const tags = store.getters['tenant/getDeviceTags']
    const tag = tags.find(t => t.id === id)
    return tag ? tag.name : id
  }

  const tagOptions = computed(() =>
    store.getters['tenant/getDeviceTags'].map(tag => ({
      value: tag.id,
      name: tag.name,
    }))
  )

  const eventCode = ref([])
  const eventDefinitionOptions = computed(() => store.getters['dmp_events_definition/getSelectedItems'])

  const typeAlarm = ref([])
  const alarmTypeOptions = computed(() => store.getters['type_alarm/getSelectedItems'])

  const eventDefinitionOptionsFilter = ref(null);
  const eventCodeFilter = ref(null)
  const typeAlarmFilter = ref(null)
  const dispatchAlarmFilter = ref(null)
  const valueFilter = ref(null)
  const idDmpProfile = ref(parseInt(router.currentRoute.params.id_dmp_profile))


  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('dmp_rules/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      id_dmp_profile: idDmpProfile.value,
      event_definition: eventDefinitionOptionsFilter.value
    })
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  const filters = ref({})
  const filtersCount = useFilterCounter(filters)

  const buildFilters = () => {
    filters.value = {
      event_definition: eventDefinitionOptionsFilter.value
    }
    refetchData()
  }

  const clearSearchFilters = () => {
    eventDefinitionOptionsFilter.value = null;
    buildFilters()
  }

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,

    idPanel,
    eventCode,
    eventDefinitionOptions,
    eventDefinitionOptionsFilter,
    typeAlarm,
    alarmTypeOptions,
    eventCodeFilter,
    typeAlarmFilter,
    dispatchAlarmFilter,
    valueFilter,

    refetchData,
    clearSearchFilters,
    buildFilters,
    filtersCount,

    idDmpProfile,
    getTagName,
    tagOptions
  }
}
